import React, { useState, useRef } from "react"
import { connect } from "react-redux"
import styled, { css } from "styled-components"
import TweenLite from "gsap/TweenLite"
import moment from "moment"
import colors from "@constants/colors"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import { h3Styles, h4Styles } from "@constants/typography"
import { RECENTLY_VIEWED_PRODUCTS_KEY } from "@constants/storageKeys"
import Layout from "@components/Layout"
import Helmet from "@components/Helmet"
import Scripts from "@views/common/Scripts"
import { useProductReviews } from "../../reviews/useProductReviews"
import ProductMetaData from "./components/ProductMetaData"
import ProductHeader from "@components/Header/ProductHeaderContent"
import ProductBlock from "./components/ProductBlock"
import DetailsBlock from "./components/DetailsBlock"
import ReviewsBlock from "./components/ReviewsBlock"
import InstallationBlock from "./components/InstallationBlock"
import ResourcesBlock from "./components/ResourcesBlock"
import ExploreBlock from "./components/ExploreBlock"
import ReviewsModal from "@components/Modal/ReviewsModal"
import WriteReviewModal from "@components/Modal/WriteReviewModal"
import ReviewSubmittedModal from "@components/Modal/ReviewSubmittedModal"
import ReviewsImageGalleryModal from "@components/Modal/ReviewsImageGalleryModal"
import ProductSelector from "@components/ProductSelector"
import VariantSelector from "@components/VariantSelector"
import DeliveryText from "./components/DeliveryText"
import ShippingText from "./components/ShippingText"
import { TextBold } from "./components/common"
import { renderRichText } from "gatsby-source-contentful/rich-text"
import { emptyRichText } from "@utils/richText"
import { withFinePrint } from "@utils/ContentfulRichText"
import { documentToHtmlString } from "@contentful/rich-text-html-renderer"
import useMultiWarehouseInventory, {
  INVENTORY_ITEM_TRACKED,
} from "@hooks/useMultiWarehouseInventory"
import useFenixShippingEstimates from "@hooks/useFenixShippingEstimates"
import useEnhancedEcommerceEvents, {
  GTMProductProps,
} from "@hooks/useEnhancedEcommerceEvents"
import useElevarEvents, {
  ElevarProductProps,
  ELEVAR_VIEW_ITEM,
} from "@hooks/useElevarEvents"
import useMaestraEvents from "@hooks/useMaestraEvents"
import useAttentiveEvents, {
  AttentiveProductProps,
} from "@hooks/useAttentiveEvents"
import { getIn, formatContentfulVariant } from "@utils/index"
import getAvailabilityByRegion from "@utils/getAvailabilityByRegion"
import { DEFAULT_DOMESTIC_SHIPPING_MESSAGE } from "@utils/inventoryRules"
import { DesktopOnly } from "@utils/MediaQuery"
import { getLocalStorage, writeLocalStorage } from "@utils/storage"
import { SELECTED_PRODUCTS_KEY } from "@constants/storageKeys"
import { Dispatch, RootState } from "@redux/store"
import atob from "atob"
import _ from "lodash-es"

const Content = styled.div``

const TabsBlock = styled.div`
  padding: 60px 0 72px;
  border-bottom: 1px solid ${colors.dividerLineGray};

  @media ${breakpoints.tablet} {
    padding: 80px 0 42px;
  }

  @media ${breakpoints.laptop} {
    flex-direction: column;
    display: flex;
    padding: 148px calc(1 / 12 * 100vw) 170px;
    border-bottom: none;
  }
`

const Tabs = styled.div`
  display: flex;
  padding: 0 20px;

  @media ${breakpoints.laptop} {
    padding: 0;
  }
`

const Tab = styled.button<{ active: boolean }>`
  ${({ active }) => css`
    ${h4Styles}
    margin-right: 6px;
    padding: 0;
    outline: none;
    border: none;
    background: none;
    font-size: 14px;
    color: ${active ? colors.black : colors.mediumGray};

    &:hover {
      cursor: pointer;
    }

    &:focus,
    &:active {
      outline: none;
    }

    @media ${breakpoints.laptop} {
      ${h3Styles}
      margin-right: 60px;
      padding: 0;
      font-size: 28px;
      line-height: 66px;
      border-bottom: ${active ? `solid 3px ${colors.black}` : "none"};
    }

    @media ${breakpoints.laptop} {
      ${h3Styles}
      margin-right: 60px;
      padding: 0;
      font-size: 32px;
      line-height: 66px;
      border-bottom: ${active ? `solid 3px ${colors.black}` : "none"};
    }
  `}
`

const TabContent = styled.div<{ active: boolean }>`
  ${({ active }) => css`
    transition: opacity ${animation.quick} ${cubicBezier}, visibility linear 0ms,
      height linear 0ms;
    visibility: ${active ? "initial" : "hidden"};
    opacity: ${active ? 1 : 0};
    height: ${active ? "initial" : "0"};
  `}
`

const Recommendations = styled.div``

enum TabTypes {
  Details = 0,
  Reviews = 1,
  Installaion = 2,
  Resources = 3,
}

const ProductDetailsPage: any = ({
  location,
  customerEmail,
  customerLocationFetched,
  customerLocation,
  automaticDiscounts,
  discountedProductsData,
  discountedProductsInventoryData,
  addToCart,
  updateCustomerZipCode,
  data,
  pageContext,
}): any => {
  const [tab, setTab] = useState<TabTypes>(TabTypes.Details)
  const [activeVariant, setActiveVariant] = useState(0)
  const [showReviews, setShowReviews] = useState<boolean>(false)
  const [showWriteReview, setWriteReview] = useState<boolean>(false)
  const [showReviewSubmitted, setReviewSubmitted] = useState<boolean>(false)
  const [showReviewsImageGallery, setShowReviewsImageGallery] =
    useState<boolean>(false)
  const [showProductSelectorConfig, setShowProductSelectorConfig] =
    useState<any>({})
  const [showProductSelector, setShowProductSelector] = useState<number>(-1)
  const [showVariantSelector, setShowVariantSelector] = useState<boolean>(false)

  const contentfulProduct = getIn(data, "contentfulProduct")
  const productData = React.useMemo(
    () => ({
      ...contentfulProduct,
      variants: contentfulProduct.variants.map(formatContentfulVariant),
    }),
    [data]
  )
  const shopifyProductId = getIn(
    productData,
    "shopifyProduct.shopifyProductId",
    ""
  )
  let decodedShopifyProductId
  try {
    decodedShopifyProductId = atob(shopifyProductId)
  } catch (error) {
    decodedShopifyProductId = shopifyProductId
  }

  const splitDecodedShopifyProductId = decodedShopifyProductId.split("/")
  const productId =
    splitDecodedShopifyProductId[splitDecodedShopifyProductId.length - 1]

  const productsInSeries = getIn(
    data,
    "relatedProductsBySeries.nodes",
    []
  ).filter(({ variants }) => variants && variants.length > 0)
  const mappedProductsInSeries = _.map(productsInSeries, (product) => ({
    productSlug: product.slug,
    productName: product.productName,
    ..._.pick(product, ["brand", "device", "devices", "series"]),
  }))

  const buysAutomaticDiscounts = automaticDiscounts.map(
    ({ customerBuysProducts }) =>
      customerBuysProducts?.indexOf(decodedShopifyProductId) > -1 ? true : false
  )
  const buysAutomaticDiscount = buysAutomaticDiscounts.find(
    (hasDiscount) => !!hasDiscount
  )
  const getsAutomaticDiscounts = automaticDiscounts.map(
    ({ customerGetsProducts }) =>
      customerGetsProducts?.indexOf(decodedShopifyProductId) > -1 ? true : false
  )
  const getsAutomaticDiscount = getsAutomaticDiscounts.find(
    (hasDiscount) => !!hasDiscount
  )

  const variants = getIn(productData, "variants")
  const validVariants = React.useMemo(
    () =>
      _.reject(variants, (variant: any) => {
        return !variant.images
      }),
    [variants]
  )
  const [validVariantsByStock, setValidVariantsByStock] =
    React.useState<any>(validVariants)

  const validDiscountVariants = React.useMemo(() => {
    if (
      (buysAutomaticDiscount || getsAutomaticDiscount) &&
      discountedProductsData
    ) {
      const discountVariants = _.flatten(
        Object.values(discountedProductsData).map(({ variants }) => variants)
      )
      return _.reject(discountVariants, (variant: any) => !variant.images)
    }
  }, [automaticDiscounts, discountedProductsData])
  const validUpsellVariants = React.useMemo(() => {
    if (productData.upsell && productData.upsellCallout) {
      return _.reject(
        productData.upsell.variants,
        (variant: any) => !variant.images
      )
    }

    return []
  }, [productData.upsell])

  const validVariantSkus = React.useMemo(() => {
    return [
      ..._.map(validVariantsByStock, "sku"),
      ..._.map(validDiscountVariants, "sku"),
      ..._.map(validUpsellVariants, "sku"),
    ]
  }, [validVariantsByStock])
  const currentVariant = validVariantsByStock[activeVariant]
  const fallbackVariant = !currentVariant ? variants[0] : undefined
  const retailPrice = getIn(
    currentVariant,
    "marketPrices.US.compareAtPrice.amount",
    undefined
  )
  const price = getIn(currentVariant, "marketPrices.US.price.amount")

  const [searchParamParsed, setSearchParamParsed] =
    React.useState<boolean>(false)
  const [variantSetBySearchParam, setVariantSetBySearchParam] =
    React.useState<boolean>(false)

  React.useEffect(() => {
    const params = location.search && location.search.split("&")
    if (params[0] && params[0].indexOf("?variant=") === 0) {
      const sku = params[0].replace("?variant=", "")
      const matchingVariant = _.findIndex(
        validVariantsByStock,
        ({ sku: variantSku }) => variantSku === sku
      )
      if (matchingVariant !== -1) {
        setActiveVariant(matchingVariant)
        setSearchParamParsed(true)
        setVariantSetBySearchParam(true)
      }
    }
  }, [])

  React.useEffect(() => {
    if (
      typeof window !== "undefined" &&
      validVariantsByStock[activeVariant] &&
      window.location.search !==
        `?variant=${validVariantsByStock[activeVariant].sku}` &&
      searchParamParsed
    ) {
      window.history?.replaceState(
        {},
        "",
        `?variant=${validVariantsByStock[activeVariant].sku}`
      )
    }
  }, [activeVariant])

  let multipleDevices
  const productDevices = getIn(productData, "devices", [])
  if (productDevices.length > 0) {
    multipleDevices = productDevices.reduce((acc, curr) => {
      if (acc) {
        acc = acc + `#${curr.name}`
      } else {
        acc = curr.name
      }

      return acc
    }, undefined)
  }

  const { onViewProductDetails, onAddProductToCart } =
    useEnhancedEcommerceEvents()

  React.useEffect(() => {
    const gtmProduct: GTMProductProps = {
      productId: getIn(productData, "shopifyProduct.shopifyProductId"),
      productName: getIn(productData, "productName"),
      variantName: getIn(currentVariant, "name"),
      sku: getIn(currentVariant, "sku"),
      price: getIn(currentVariant, "marketPrices.US.price.amount"),
      brand: getIn(productData, "brand.name"),
      device: multipleDevices
        ? multipleDevices.replace("#", ", ")
        : getIn(productData, "device.name"),
      deviceType: getIn(productData, "device.type.name"),
      list: "Product Details",
    }

    onViewProductDetails(gtmProduct)
  }, [])

  const headerRef: any = useRef()
  const onRequestScrollToTop = () => {
    const html = document.getElementsByTagName("html")
    if (html && html[0]) {
      TweenLite.to(html[0], 0.4, { scrollTop: 0 })
    }
  }
  const onRequestScrollToReviews = () => {
    const html = document.getElementsByTagName("html")
    const tabs = document.getElementById("tabs-block")

    if (tabs) {
      TweenLite.to(html[0], 0.4, { scrollTop: tabs?.offsetTop })
    }
  }

  const { avgRating, numReviews, goodReview, reviews } = useProductReviews(
    productId,
    { per_page: 20 }
  )

  const [initialReviewImage, setInitialReviewImage] = React.useState<any>(null)

  const openReviewsImageGallery = (id) => {
    setShowReviewsImageGallery(true)
    setInitialReviewImage(id)
  }

  const closeReviewsImageGallery = () => {
    setShowReviewsImageGallery(false)
    setInitialReviewImage(null)
  }

  const reviewImages = React.useMemo(() => {
    return _.flatten(
      _.map(reviews, (review) => {
        return review.images_data
          ? _.map(review.images_data, (data) => {
              return {
                id: data.id,
                review: review.id,
                alt: review.title,
                image: data.original_url,
                thumbnail: data.thumb_url,
                onClick: () => {
                  openReviewsImageGallery(data.id)
                },
              }
            })
          : []
      })
    ).filter((image) => !!image)
  }, [reviews])

  const seoTitle = productData?.seoTitle
  const seoDescription = productData?.seoDescription?.seoDescription
  const seoKeywords = productData?.seoKeywords

  let description = seoDescription
  if (!description) {
    const productDescription = getIn(productData, "productDescription.raw")
    const htmlString: string = productDescription
      ? documentToHtmlString(productDescription)
      : ""
    description = htmlString.replace(/<[^>]+>/g, " ")
  }

  const productMetaData: any = {
    url: getIn(contentfulProduct, "slug"),
    name: getIn(contentfulProduct, "productName"),
    description,
    keywords: seoKeywords,
    price: getIn(contentfulProduct, "variants.0.marketPrices.US.price.amount"),
    sku: getIn(contentfulProduct, "variants.0.sku"),
    images: _.map(
      getIn(contentfulProduct, "variants.0.images", []),
      ({ file }) => file.url
    ),
  }

  if (avgRating > 0 && numReviews > 0) {
    productMetaData.ratingValue =
      avgRating > 4 ? _.toString(avgRating) : undefined
    productMetaData.reviewCount = _.toString(numReviews)
  }

  if (goodReview) {
    productMetaData.reviewAuthor = getIn(
      goodReview,
      "user.display_name",
      "Anonymous"
    )
    productMetaData.reviewRating = _.toString(goodReview.score)
    productMetaData.reviewBody = goodReview.content
  }

  const { inventoryData } = useMultiWarehouseInventory(validVariantSkus)

  const { fenixShippingMethods, countryProhibitedProducts } = getIn(
    data,
    "contentfulGlobalSettings",
    {}
  )

  const { customerZipCode } = customerLocation

  const [availabilityData, setAvailabilityData] = React.useState<any>({})

  React.useEffect(() => {
    if (
      customerLocationFetched &&
      customerLocation?.customerCountryCode &&
      inventoryData &&
      _.keys(inventoryData).length > 0
    ) {
      const currentSku = getIn(currentVariant, "sku", "")
      const availabilityByRegion = getAvailabilityByRegion(
        customerLocation.customerCountryCode,
        currentSku,
        inventoryData
      )
      setAvailabilityData(availabilityByRegion)
    }
  }, [inventoryData, customerLocationFetched, customerLocation, currentVariant])

  React.useEffect(() => {
    setDefaultShippingComponent(
      <TextBold>
        {availabilityData?.shippingMessage || DEFAULT_DOMESTIC_SHIPPING_MESSAGE}
      </TextBold>
    )
  }, [availabilityData])

  const [DefaultShippingComponent, setDefaultShippingComponent] =
    React.useState<React.ReactNode | null>(null)

  const [checkInitialAvailability, setCheckInitialAvailability] =
    React.useState<boolean>(false)

  React.useEffect(() => {
    if (
      !checkInitialAvailability &&
      customerLocationFetched &&
      customerLocation?.customerCountryCode &&
      inventoryData &&
      _.keys(inventoryData).length > 0
    ) {
      const variantsWithAvailability = validVariants.map((variant) => {
        const dataForSku = getAvailabilityByRegion(
          customerLocation.customerCountryCode,
          variant.sku,
          inventoryData
        )
        return {
          ...variant,
          isUnavailable: !dataForSku?.isAvailable,
        }
      })
      const variantsSortedByStock = _.sortBy(
        variantsWithAvailability,
        ({ isUnavailable }) => {
          return isUnavailable
        }
      )

      setValidVariantsByStock(variantsSortedByStock)

      if (activeVariant !== 0 && !variantSetBySearchParam) {
        setActiveVariant(0)
      } else if (variantSetBySearchParam) {
        const params = location.search && location.search.split("&")
        const sku = params[0].replace("?variant=", "")
        const matchingVariant = _.findIndex(
          validVariantsByStock,
          ({ sku: variantSku }) => variantSku === sku
        )

        if (matchingVariant !== -1 && matchingVariant !== activeVariant) {
          setActiveVariant(matchingVariant)
        }
      }

      setCheckInitialAvailability(true)
    }
  }, [
    customerLocationFetched,
    customerLocation,
    inventoryData,
    availabilityData,
    variantSetBySearchParam,
  ])

  const [deliveryText, setDeliveryText] = React.useState<
    React.ReactNode | undefined
  >(undefined)
  const [shippingText, setShippingText] = React.useState<React.ReactNode>(
    DefaultShippingComponent
  )

  const shippingMethods = fenixShippingMethods || [
    "FedEx 2Day",
    "FedEx Next Day",
    "USPS Shipping",
    "USPS Free Shipping",
  ]

  const {
    invalidShippingData,
    shippingDataLoaded,
    shippingData,
    zipCodeError,
    zipCodeErrorMessage,
    getShippingDataForSku,
  } = useFenixShippingEstimates(customerZipCode, validVariants, shippingMethods)

  React.useEffect(() => {
    if (!availabilityData?.isAvailable || !customerZipCode || zipCodeError) {
      zipCodeErrorMessage
        ? setShippingText(<TextBold>{zipCodeErrorMessage}</TextBold>)
        : setShippingText(DefaultShippingComponent)
      setDeliveryText(undefined)
      return
    }

    const variantShippingData = getShippingDataForSku(currentVariant.sku)

    if (variantShippingData) {
      const deliveryDate = moment(
        variantShippingData.guaranteedDeliveryDate
      ).format("ddd, MMM D,")
      const orderWithin = `${variantShippingData.hours}hr ${variantShippingData.minutes}min`
      setShippingText(<ShippingText />)
      setDeliveryText(
        <DeliveryText deliveryDate={deliveryDate} orderWithin={orderWithin} />
      )
    } else {
      setDeliveryText(undefined)
    }
  }, [
    customerZipCode,
    invalidShippingData,
    shippingDataLoaded,
    shippingData,
    zipCodeError,
    zipCodeErrorMessage,
    currentVariant,
    customerLocationFetched,
    customerLocation,
    inventoryData,
    availabilityData,
  ])

  React.useEffect(() => {
    const recentProducts = getLocalStorage(RECENTLY_VIEWED_PRODUCTS_KEY) || []
    const imageSrc = getIn(productData, "variants.0.images.0", "")
    const [cleanedSrc] = imageSrc.split("?")

    const dataToStore = {
      productSlug: getIn(productData, "slug"),
      productName: getIn(productData, "productName"),
      price: getIn(currentVariant, "marketPrices.US.price.amount"),
      compareAtPrice: getIn(
        currentVariant,
        "marketPrices.US.compareAtPrice.amount"
      ),
      marketPrices: {
        US: {
          price: {
            amount: getIn(currentVariant, "marketPrices.US.price.amount"),
            currencyCode: "USD",
          },
          compareAtPrice: null,
        },
      },
      image: `https:${cleanedSrc}`,
    }

    try {
      writeLocalStorage(
        RECENTLY_VIEWED_PRODUCTS_KEY,
        _.slice(_.uniqBy([dataToStore, ...recentProducts], "productSlug"), 0, 6)
      )
    } catch (error) {
      writeLocalStorage(RECENTLY_VIEWED_PRODUCTS_KEY, [dataToStore])
    }
  }, [])

  const inventoryLoaded =
    customerLocationFetched &&
    customerLocation &&
    inventoryData &&
    Object.keys(inventoryData).length > 0

  const { elevarOnViewItem, elevarOnAddToCart } = useElevarEvents()
  const { maestraOnViewProduct } = useMaestraEvents()
  const { attentiveOnViewItem } = useAttentiveEvents()

  React.useEffect(() => {
    if (inventoryLoaded) {
      const variantId = getIn(currentVariant, "variantId")

      if (variantId) {
        maestraOnViewProduct({
          product: getIn(productData, "shopifyProduct.shopifyProductId"),
          user: { email: customerEmail },
        })
      }
    }
  }, [inventoryLoaded])

  const hasAvailabilityData = Object.keys(availabilityData).length === 3
  const [eventsTriggered, setEventsTriggered] = React.useState<boolean>(false)

  React.useEffect(() => {
    if (inventoryLoaded && hasAvailabilityData && !eventsTriggered) {
      const variantId = getIn(currentVariant, "variantId")

      if (variantId) {
        const sku = getIn(currentVariant, "sku")
        const imageSrc = getIn(currentVariant, "images.0", "")
        const [cleanedSrc] = imageSrc.split("?")

        const attentiveProduct: AttentiveProductProps = {
          sku,
          productId: getIn(productData, "shopifyProduct.shopifyProductId"),
          variantId: variantId,
          productName: getIn(productData, "productName"),
          variantName: getIn(currentVariant, "name"),
          image: `https:${cleanedSrc}`,
          price: getIn(currentVariant, "marketPrices.US.price.amount"),
          brand: "Urban Armor Gear",
          device: multipleDevices
            ? multipleDevices.replace("#", ", ")
            : getIn(productData, "device.name"),
          deviceType: getIn(productData, "device.type.name"),
        }

        attentiveOnViewItem(attentiveProduct)
        setEventsTriggered(true)
      }
    }
  }, [inventoryLoaded, availabilityData, currentVariant])

  React.useEffect(() => {
    const sendViewItemOnce = (product, list) => {
      const dataLayer = getIn(window, "dataLayer", [])
      const eventDataIndex = _.findLastIndex(
        dataLayer,
        ({ event }) => event === ELEVAR_VIEW_ITEM
      )
      const productData = _.get(
        dataLayer,
        `${eventDataIndex}.ecommerce.detail.products.0`
      )

      if (productData?.sku !== product.sku) {
        elevarOnViewItem(product, list)
      }
    }

    if (inventoryLoaded && hasAvailabilityData) {
      const variantId = getIn(currentVariant, "variantId")

      if (variantId) {
        const selectedProducts = getLocalStorage(SELECTED_PRODUCTS_KEY) || {}
        const list =
          selectedProducts[
            getIn(productData, "shopifyProduct.shopifyProductId")
          ]

        const sku = getIn(currentVariant, "sku")
        const imageSrc = getIn(currentVariant, "images.0", "")
        const [cleanedSrc] = imageSrc.split("?")
        const inventory = availabilityData?.quantity || 0

        const elevarProduct: ElevarProductProps = {
          sku,
          productId: getIn(productData, "shopifyProduct.shopifyProductId"),
          variantId: variantId,
          productName: getIn(productData, "productName"),
          variantName: getIn(currentVariant, "name"),
          image: `https:${cleanedSrc}`,
          price: getIn(currentVariant, "marketPrices.US.price.amount"),
          compareAtPrice: getIn(
            currentVariant,
            "marketPrices.US.compareAtPrice.amount",
            "0.00"
          ),
          brand: "Urban Armor Gear",
          device: multipleDevices
            ? multipleDevices.replace("#", ", ")
            : getIn(productData, "device.name"),
          deviceType: getIn(productData, "device.type.name"),
          inventory,
          list,
        }

        sendViewItemOnce(elevarProduct, list)
      }
    }
  }, [inventoryLoaded, hasAvailabilityData, currentVariant])

  const onAddToCart = React.useCallback(() => {
    let multipleDevices
    const productDevices = getIn(productData, "devices", [])
    if (productDevices.length > 0) {
      multipleDevices = productDevices.reduce((acc, curr) => {
        if (acc) {
          acc = acc + `#${curr.name}`
        } else {
          acc = curr.name
        }

        return acc
      }, undefined)
    }

    const variantId = getIn(currentVariant, "variantId")

    if (variantId) {
      const imageSrc = getIn(currentVariant, "images.0", "")
      const [cleanedSrc] = imageSrc.split("?")

      addToCart({
        cartProduct: {
          contentfulProductId: getIn(productData, "contentful_id"),
          contentfulVariantId: getIn(currentVariant, "contentfulVariantId"),
          shopifyProductId: getIn(
            productData,
            "shopifyProduct.shopifyProductId"
          ),
          shopifyVariantId: variantId,
          bundleProduct: getIn(productData, "bundleProduct", false),
          productSubType: getIn(productData, "productSubType.slug"),
          productSlug: getIn(productData, "slug"),
          productName: getIn(productData, "productName"),
          variantName: getIn(currentVariant, "name"),
          variantDevice: getIn(currentVariant, "deviceName"),
          device: multipleDevices
            ? multipleDevices.replace("#", ", ")
            : getIn(productData, "device.name"),
          deviceType: getIn(productData, "device.type.name"),
          brand: getIn(productData, "brand.name"),
          builtInMagnet: getIn(productData, "builtInMagnet", false),
          language: getIn(productData, "language"),
          color: getIn(currentVariant, "color"),
          size: getIn(currentVariant, "size"),
          sku: getIn(currentVariant, "sku"),
          price: getIn(currentVariant, "marketPrices.US.price.amount"),
          compareAtPrice: getIn(
            currentVariant,
            "marketPrices.US.compareAtPrice.amount"
          ),
          marketPrices: getIn(currentVariant, "marketPrices"),
          image: `https:${cleanedSrc}`,
        },
      })

      const selectedProducts = getLocalStorage(SELECTED_PRODUCTS_KEY) || {}
      const list =
        selectedProducts[
          getIn(productData, "shopifyProduct.shopifyProductId")
        ] || "direct"

      const elevarProduct: ElevarProductProps = {
        sku: getIn(currentVariant, "sku"),
        productId: getIn(productData, "shopifyProduct.shopifyProductId"),
        variantId: variantId,
        productName: getIn(productData, "productName"),
        variantName: getIn(currentVariant, "name"),
        image: `https:${cleanedSrc}`,
        price: getIn(currentVariant, "marketPrices.US.price.amount"),
        brand: "Urban Armor Gear",
        device: multipleDevices
          ? multipleDevices.replace("#", ", ")
          : getIn(productData, "device.name"),
        deviceType: getIn(productData, "device.type.name"),
        quantity: "1",
        list,
      }

      elevarOnAddToCart(elevarProduct, list)
    }

    const gtmProduct: GTMProductProps = {
      productId: getIn(productData, "shopifyProduct.shopifyProductId"),
      productName: getIn(productData, "productName"),
      variantName: getIn(currentVariant, "name"),
      sku: getIn(currentVariant, "sku"),
      price: getIn(currentVariant, "marketPrices.US.price.amount"),
      brand: getIn(productData, "brand.name"),
      device: multipleDevices
        ? multipleDevices.replace("#", ", ")
        : getIn(productData, "device.name"),
      deviceType: getIn(productData, "device.type.name"),
      quantity: 1,
    }

    onAddProductToCart(gtmProduct)
  }, [productData, currentVariant, inventoryLoaded])

  const installationText = getIn(productData, "installationText")
  const installationMedia = getIn(productData, "installationMedia")
  const installationImage = getIn(productData, "installationImage")
  const hasInstallationTab =
    !!installationText && (installationMedia || installationImage)
  const resourcesText =
    getIn(currentVariant, "resourcesText") ||
    getIn(productData, "resourcesText")
  const resourcesMedia =
    getIn(currentVariant, "resourcesMedia") ||
    getIn(productData, "resourcesMedia")
  const safetyText = getIn(productData, "safetyText")
  const safetyMedia = getIn(productData, "safetyMedia")
  const hasResourcesTab =
    !!resourcesText || resourcesMedia || !!safetyText || safetyMedia

  const onUpdateZipCode = (zipCode?: string) => {
    if (zipCode) {
      updateCustomerZipCode(zipCode)
    }
  }

  const automaticDiscountData = data.globalData.automaticDiscounts

  const allDiscountProductsIndexIdMap = {}
  const allDiscountProducts: any = []

  automaticDiscounts.forEach((automaticDiscount, i) => {
    const discountProducts: any = []

    if (
      automaticDiscount.customerBuysProducts &&
      automaticDiscount.customerBuysProducts.indexOf(decodedShopifyProductId) >
        -1
    ) {
      automaticDiscounts[i].bogoType = "X"
      automaticDiscount.customerGetsProducts.forEach((id) => {
        const contentfulId = automaticDiscount.idMap[id]

        if (discountedProductsData[contentfulId]) {
          discountProducts.push(discountedProductsData[contentfulId])
        }
      })
    } else if (
      automaticDiscount.customerGetsProducts &&
      automaticDiscount.customerGetsProducts.indexOf(decodedShopifyProductId) >
        -1
    ) {
      automaticDiscounts[i].bogoType = "Y"
      automaticDiscount.customerBuysProducts.forEach((id) => {
        const contentfulId = automaticDiscount.idMap[id]

        if (discountedProductsData[contentfulId]) {
          discountProducts.push(discountedProductsData[contentfulId])
        }
      })
    }

    allDiscountProducts.push(discountProducts)
    allDiscountProductsIndexIdMap[i] = automaticDiscount.id
  })

  const formattedDiscountProducts = React.useMemo(() => {
    if (allDiscountProducts.length === 0) {
      return allDiscountProducts
    }

    let allFilteredProducts: any = []

    allDiscountProducts.forEach((discountProducts, i) => {
      let filteredProducts = discountProducts.filter(({ variants }) => {
        const hasImage = getIn(variants, "0.images.0.file.url")
        return variants && variants.length > 0 && hasImage
      })
      const discountId = allDiscountProductsIndexIdMap[i]
      const parsedDiscountId = discountId.replace(
        "gid://shopify/DiscountAutomaticNode/",
        ""
      )
      const discountConfig = automaticDiscountData?.find(
        ({ discountId }) => discountId === parsedDiscountId
      )

      if (!discountConfig) {
        return
      } else if (productData.device.slug === "portable-charger") {
        filteredProducts = filteredProducts.filter((p) => {
          return !!p.builtInMagnet
        })
      } else if (discountConfig.matchDevice) {
        const hasMagnet = productData.builtInMagnet

        filteredProducts = filteredProducts.filter((p) => {
          return hasMagnet
            ? p.device.slug === productData.device.slug ||
                p.device.slug === "portable-charger"
            : p.device.slug === productData.device.slug
        })
      } else if (
        productData?.productSubType?.slug &&
        ["cases", "screen-protectors"].indexOf(
          productData.productSubType.slug
        ) > -1
      ) {
        filteredProducts = filteredProducts.filter((p) => {
          return p.productSubType &&
            ["cases", "screen-protectors"].indexOf(p.productSubType.slug) > -1
            ? p.device.slug === productData.device.slug
            : true
        })
      }

      allFilteredProducts.push(
        filteredProducts.map((p) => {
          const imageSrc = getIn(p, "variants.0.images.0.file.url", "")
          const [cleanedSrc] = imageSrc.split("?")
          const productImage = `https:${cleanedSrc}`
          const productPrice = getIn(
            p,
            "variants.0.marketPrices.US.price.amount",
            ""
          )
          const productCompareAtPrice = getIn(
            p,
            "variants.0.marketPrices.US.compareAtPrice.amount"
          )

          return {
            ...p,
            productImage,
            productPrice,
            productCompareAtPrice,
          }
        })
      )
    })

    return allFilteredProducts
  }, [allDiscountProducts, customerLocationFetched, customerLocation])

  const upsellProduct = React.useMemo(() => {
    if (!productData.upsell || !productData.upsellCallout) {
      return undefined
    }

    let productImage
    let productPrice
    let productCompareAtPrice

    const formattedVariants = productData.upsell.variants.map(
      formatContentfulVariant
    )

    const variantsWithClickHandlers = formattedVariants.map((v, i) => {
      if (i === 0) {
        const imageSrc = getIn(v, "images.0", "")
        const [cleanedSrc] = imageSrc.split("?")
        productImage = `https:${cleanedSrc}`
        productPrice = getIn(v, "marketPrices.US.price.amount")
        productCompareAtPrice = getIn(
          v,
          "marketPrices.US.compareAtPrice.amount"
        )
      }

      const onAddToCart = (quantity) => {
        let multipleDevices
        const productDevices = productData.upsell.devices || []

        if (productDevices.length > 0) {
          multipleDevices = productDevices.reduce((acc, curr) => {
            if (acc) {
              acc = acc + `#${curr.name}`
            } else {
              acc = curr.name
            }

            return acc
          }, undefined)
        }

        const imageSrc = getIn(v, "images.0", "")
        const [cleanedSrc] = imageSrc.split("?")

        addToCart({
          cartProduct: {
            contentfulProductId: productData.upsell.contentful_id,
            contentfulVariantId: v.contentfulVariantId,
            shopifyProductId:
              productData.upsell.shopifyProduct?.shopifyProductId,
            shopifyVariantId: v.variantId,
            customPrint: productData.upsell.customPrint,
            bundleProduct: productData.upsell.bundleProduct,
            productSubType: productData.productSubType?.slug,
            productSlug: productData.upsell.slug,
            productName: productData.upsell.productName,
            variantName: v.name,
            variantDevice: getIn(v, "device.name"),
            device: multipleDevices
              ? multipleDevices.replace("#", ", ")
              : productData.upsell.device?.name,
            deviceType: productData.upsell.device?.type?.name,
            brand: productData.upsell.brand?.name,
            builtInMagnet: !!productData.builtInMagnet,
            language: productData.upsell.language,
            color: v.color,
            size: v.size,
            sku: v.sku,
            price: getIn(v, "marketPrices.US.price.amount"),
            compareAtPrice: getIn(v, "marketPrices.US.compareAtPrice.amount"),
            marketPrices: v.marketPrices,
            image: `https:${cleanedSrc}`,
            quantity,
          },
        })

        const elevarProduct: ElevarProductProps = {
          sku: v.sku,
          productId: productData.upsell.shopifyProduct?.shopifyProductId,
          variantId: v.variantId,
          productName: productData.upsell.productName,
          variantName: v.name,
          image: `https:${cleanedSrc}`,
          price: getIn(v, "marketPrices.US.price.amount"),
          brand: "Urban Armor Gear",
          device: multipleDevices
            ? multipleDevices.replace("#", ", ")
            : productData.upsell.device?.name,
          deviceType: productData.upsell.device?.type?.name,
          quantity,
          list: "upsell",
        }

        elevarOnAddToCart(elevarProduct, "upsell")

        const gtmProduct: GTMProductProps = {
          productId: productData.upsell.shopifyProduct?.shopifyProductId,
          productName: productData.upsell.productName,
          variantName: v.name,
          sku: v.sku,
          price: getIn(v, "marketPrices.US.price.amount"),
          brand: productData.upsell.brand.name,
          device: multipleDevices
            ? multipleDevices.replace("#", ", ")
            : productData.upsell.device.name,
          deviceType: productData.upsell.device?.type?.name,
          quantity,
        }

        onAddProductToCart(gtmProduct)
      }

      return {
        ...v,
        onAddToCart,
      }
    })

    return {
      ...productData.upsell,
      productImage,
      productPrice,
      productCompareAtPrice,
      variants: variantsWithClickHandlers,
    }
  }, [productData.upsell])

  const availableSkusByRegion = React.useMemo(() => {
    const combinedInventoryData = {
      ...inventoryData,
      ...discountedProductsInventoryData,
    }
    const discountVariants = _.flatten(
      Object.values(discountedProductsData).map(({ variants }) => variants)
    )
    const upsellVariants = upsellProduct?.variants || []
    const allVariants = [...discountVariants, ...upsellVariants]
    const availableSkus: any = []

    allVariants.forEach(({ sku }) => {
      const tracked = getIn(
        combinedInventoryData,
        `${sku}.${INVENTORY_ITEM_TRACKED}`,
        true
      )

      if (
        tracked &&
        getAvailabilityByRegion(
          customerLocation.customerCountryCode,
          sku,
          combinedInventoryData
        ).isAvailable
      ) {
        availableSkus.push(sku)
      }
    })

    return availableSkus
  }, [
    inventoryData,
    discountedProductsData,
    discountedProductsInventoryData,
    upsellProduct,
    customerLocationFetched,
    customerLocation,
  ])

  const openProductSelector = (i, id) => {
    const parsedDiscountId = id.replace(
      "gid://shopify/DiscountAutomaticNode/",
      ""
    )
    const discountConfig = automaticDiscountData?.find(
      ({ discountId }) => discountId === parsedDiscountId
    )

    setShowProductSelectorConfig(discountConfig)
    setShowProductSelector(i)
  }

  const closeProductSelector = () => {
    setShowProductSelector(-1)
  }

  const openVariantSelector = () => {
    setShowVariantSelector(true)
  }

  const closeVariantSelector = () => {
    setShowVariantSelector(false)
  }

  const showDiscountBlock =
    (buysAutomaticDiscount || getsAutomaticDiscount) &&
    automaticDiscountData &&
    formattedDiscountProducts &&
    formattedDiscountProducts.find(
      (discountProducts) => discountProducts.length > 0
    )

  const showProp65Button =
    customerLocation?.customerStateCode === "CA" &&
    getIn(currentVariant, "showProp65Warning", false)

  const getDescriptionWithTemplateStrings = () => {
    const rawDescription = getIn(
      productData,
      "productDescription",
      emptyRichText
    )
    let formattedRaw = rawDescription?.raw

    if (rawDescription?.raw.indexOf("%DEVICE%") > -1) {
      if (currentVariant?.deviceName) {
        formattedRaw = rawDescription.raw.replace(
          /%DEVICE%/g,
          currentVariant.deviceName
        )
      } else {
        formattedRaw = rawDescription.raw.replaceAll(
          /%DEVICE%/g,
          productData?.device?.name
        )
      }
    }

    return {
      raw: formattedRaw,
    }
  }

  const templatedDescription = getDescriptionWithTemplateStrings()

  const [notAvailableInCustomerCountry, setNotAvailableInCustomerCountry] =
    React.useState<boolean>(false)
  const [
    notAvailableInCustomerCountryText,
    setNotAvailableInCustomerCountryText,
  ] = React.useState<undefined | string>(undefined)

  React.useEffect(() => {
    if (
      customerLocationFetched &&
      customerLocation &&
      countryProhibitedProducts &&
      countryProhibitedProducts.length > 0
    ) {
      countryProhibitedProducts.forEach((rule) => {
        const countries = getIn(rule, "countries", [])
        const products = getIn(rule, "products", [])

        if (countries.includes(customerLocation.customerCountryCode)) {
          const prohibitedProduct = products.find(
            ({ slug }) => slug === productData.slug
          )

          if (prohibitedProduct) {
            setNotAvailableInCustomerCountry(true)
            setNotAvailableInCustomerCountryText(rule.message)
          }
        }
      })
    }
  }, [countryProhibitedProducts, customerLocationFetched, customerLocation])

  return (
    <Layout
      location={location}
      data={data}
      showFixedHeader
      fixedHeaderTopRef={headerRef}
      FixedHeaderComponent={
        <ProductHeader
          notAvailableInCountry={notAvailableInCustomerCountry}
          notAvailable={productData?.notAvailable}
          notAvailableButtonText={productData?.notAvailableButtonText}
          outOfStockButtonText={productData?.outOfStockButtonText}
          isAvailable={availabilityData.isAvailable}
          productPrice={price}
          productName={getIn(productData, "productName")}
          deviceName={getIn(productData, "device.name")}
          color={{
            name: getIn(currentVariant, "name"),
            hexString: getIn(currentVariant, "hexString"),
            swatchImage: getIn(currentVariant, "swatchImage"),
          }}
          onClickDevice={onRequestScrollToTop}
          onClickAddToCart={onAddToCart}
        />
      }
    >
      <Helmet
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
      />
      <Scripts />
      <ProductMetaData {...productMetaData} />
      <Content>
        {currentVariant && (
          <ProductBlock
            globalData={getIn(data, "globalData")}
            headerRef={headerRef}
            productName={getIn(productData, "productName")}
            retailPrice={retailPrice}
            price={price}
            avgRating={avgRating}
            numReviews={numReviews}
            multipleDevices={!!multipleDevices}
            device={
              multipleDevices
                ? multipleDevices
                : getIn(productData, "device.name")
            }
            useDeviceAnchor={productData?.useDeviceAnchor || false}
            deviceAnchorLabel={productData?.deviceAnchorLabel || ""}
            brand={getIn(productData, "brand.name")}
            images={_.map(
              getIn(productData, "images", []),
              ({ file }) => file.url
            )}
            variants={validVariantsByStock}
            currentVariant={currentVariant}
            fallbackVariant={fallbackVariant}
            setActiveVariant={(index) => setActiveVariant(index)}
            productsInSeries={mappedProductsInSeries}
            notAvailableInCountry={notAvailableInCustomerCountry}
            notAvailableInCountryText={notAvailableInCustomerCountryText}
            notAvailable={productData?.notAvailable}
            notAvailableButtonText={productData?.notAvailableButtonText}
            outOfStockButtonText={productData?.outOfStockButtonText}
            isAvailable={availabilityData.isAvailable}
            loadingInventory={!inventoryLoaded}
            customerZipCode={customerZipCode}
            customerLocationFetched={customerLocationFetched}
            shippingDataLoaded={shippingDataLoaded}
            invalidShippingData={invalidShippingData}
            deliveryText={deliveryText}
            shippingText={shippingText}
            hideZipCodeForm={
              (customerLocation?.customerCountryCode &&
                customerLocation.customerCountryCode === "US") ||
              !availabilityData.isAvailable
            }
            addDiscountedProductToCart={addToCart}
            addToCart={onAddToCart}
            onUpdateZipCode={onUpdateZipCode}
            onClickRatings={(type) => {
              if (type === "mobile") {
                setShowReviews(true)
              } else {
                onRequestScrollToReviews()
                setTab(TabTypes.Reviews)
              }
            }}
            showDiscountBlock={showDiscountBlock}
            discountProducts={formattedDiscountProducts}
            discountProductsIdMap={allDiscountProductsIndexIdMap}
            automaticDiscounts={automaticDiscounts}
            upsellProduct={upsellProduct}
            upsellCallout={productData.upsellCallout}
            availableVariants={availableSkusByRegion}
            openProductSelector={openProductSelector}
            openVariantSelector={openVariantSelector}
            lifestyleMuxVideos={getIn(productData, "lifestyleMuxVideos")}
            imageGalleryMuxVideos={getIn(productData, "imageGalleryMuxVideos")}
          />
        )}
        <TabsBlock id={"tabs-block"}>
          <Tabs>
            <Tab
              onClick={() => {
                setTab(TabTypes.Details)
              }}
              active={tab === TabTypes.Details}
            >
              Details
            </Tab>
            <DesktopOnly>
              <Tab
                onClick={() => {
                  setTab(TabTypes.Reviews)
                }}
                active={tab === TabTypes.Reviews}
              >
                Reviews
              </Tab>
            </DesktopOnly>
            {hasInstallationTab && (
              <Tab
                onClick={() => {
                  setTab(TabTypes.Installaion)
                }}
                active={tab === TabTypes.Installaion}
              >
                Installation
              </Tab>
            )}
            {hasResourcesTab && (
              <Tab
                onClick={() => {
                  setTab(TabTypes.Resources)
                }}
                active={tab === TabTypes.Resources}
              >
                Resources
              </Tab>
            )}
          </Tabs>
          <TabContent active={tab === TabTypes.Details}>
            <DetailsBlock
              description={renderRichText(templatedDescription, withFinePrint)}
              deviceAnchorText={
                productData?.useDeviceAnchor
                  ? renderRichText(
                      getIn(productData, "deviceAnchorText", emptyRichText),
                      withFinePrint
                    )
                  : null
              }
              features={getIn(productData, "featuresBlock", [])}
              specs={
                getIn(currentVariant, "specs.productSpecification") ||
                getIn(productData, "specs.productSpecification", [])
              }
              specSheets={
                getIn(currentVariant, "specSheets") ||
                getIn(productData, "specSheets", [])
              }
              avgRating={avgRating}
              numReviews={numReviews}
              setShowReviews={(value: boolean) => setShowReviews(value)}
              showProp65Button={showProp65Button}
              prop65Text={renderRichText(
                getIn(
                  data,
                  "contentfulGlobalSettings.prop65Text",
                  emptyRichText
                ),
                withFinePrint
              )}
            />
          </TabContent>
          <DesktopOnly>
            <TabContent active={tab === TabTypes.Reviews}>
              <ReviewsBlock
                reviews={reviews}
                reviewImages={reviewImages}
                avgRating={avgRating}
                numReviews={numReviews}
                setShowReviews={(value: boolean) => setShowReviews(value)}
                setWriteReview={(value: boolean) => setWriteReview(value)}
                onClickImage={openReviewsImageGallery}
                onClickCount={() => {
                  if (reviewImages.length > 0) {
                    openReviewsImageGallery(reviewImages[0].id)
                  }
                }}
              />
            </TabContent>
          </DesktopOnly>
          {hasInstallationTab && (
            <TabContent active={tab === TabTypes.Installaion}>
              <InstallationBlock
                text={
                  installationText
                    ? renderRichText(
                        installationText || emptyRichText,
                        withFinePrint
                      )
                    : undefined
                }
                media={installationMedia}
                image={installationImage}
              />
            </TabContent>
          )}
          {hasResourcesTab && (
            <TabContent active={tab === TabTypes.Resources}>
              {resourcesMedia && (
                <ResourcesBlock
                  text={
                    resourcesText
                      ? renderRichText(
                          resourcesText || emptyRichText,
                          withFinePrint
                        )
                      : undefined
                  }
                  media={resourcesMedia}
                />
              )}
              {safetyMedia && (
                <ResourcesBlock
                  text={
                    safetyText
                      ? renderRichText(
                          safetyText || emptyRichText,
                          withFinePrint
                        )
                      : undefined
                  }
                  media={safetyMedia}
                />
              )}
            </TabContent>
          )}
        </TabsBlock>
        <ExploreBlock cards={getIn(productData, "highlightCards", [])} />
        <Recommendations
          dangerouslySetInnerHTML={{
            __html: `<div id="frosmo-product-reco-1"></div><div id="frosmo-product-reco-2"></div>`,
          }}
        />
        <ReviewsModal
          productId={productId}
          isOpen={showReviews}
          onRequestClose={() => {
            setShowReviews(false)
            setWriteReview(false)
            setReviewSubmitted(false)
          }}
          reviews={reviews}
          avgRating={avgRating}
          numReviews={numReviews}
          onClickWriteReview={() => {
            setShowReviews(false)
            setWriteReview(true)
            setReviewSubmitted(false)
          }}
          onRequestOpenReviewImageGallery={openReviewsImageGallery}
        />
        <WriteReviewModal
          productId={productId}
          productTitle={getIn(productData, "productName")}
          isOpen={showWriteReview}
          onRequestClose={() => {
            setShowReviews(false)
            setWriteReview(false)
            setReviewSubmitted(false)
          }}
          onSubmit={() => {
            setShowReviews(false)
            setWriteReview(false)
            setReviewSubmitted(true)
          }}
          backToReviews={() => {
            setShowReviews(true)
            setWriteReview(false)
            setReviewSubmitted(false)
          }}
        />
        <ReviewSubmittedModal
          isOpen={showReviewSubmitted}
          onRequestClose={() => {
            setShowReviews(false)
            setWriteReview(false)
            setReviewSubmitted(false)
          }}
        />
        <ReviewsImageGalleryModal
          isOpen={showReviewsImageGallery}
          reviews={reviews}
          initialImage={initialReviewImage}
          onRequestClose={closeReviewsImageGallery}
        />
        {showDiscountBlock && (
          <ProductSelector
            isOpen={showProductSelector > -1}
            header={
              showProductSelectorConfig[
                `pdpMessage${showProductSelectorConfig.bogoType}`
              ] || ""
            }
            deal={
              showProductSelectorConfig[
                `pdpMessage${showProductSelectorConfig.bogoType}`
              ] || ""
            }
            products={
              showProductSelector > -1
                ? formattedDiscountProducts[showProductSelector]
                : []
            }
            availableVariants={availableSkusByRegion}
            addToCart={addToCart}
            onRequestClose={closeProductSelector}
            closeOnAddToCart
          />
        )}
        {!showDiscountBlock && upsellProduct && (
          <VariantSelector
            isOpen={showVariantSelector}
            product={productData.upsell}
            availableVariants={availableSkusByRegion}
            addToCart={addToCart}
            onRequestClose={closeVariantSelector}
          />
        )}
      </Content>
    </Layout>
  )
}

const mapState = (state: RootState) => ({
  customerEmail: state.customer.data?.email || "",
  customerLocationFetched: state.customer.customerLocationFetched,
  customerLocation: state.customer.customerLocation,
  automaticDiscounts: state.discounts.automaticDiscounts,
  discountedProductsData: state.discounts.discountedProductsData,
  discountedProductsInventoryData:
    state.discounts.discountedProductsInventoryData,
})

const mapDispatch = (dispatch: Dispatch) => ({
  addToCart: dispatch.checkout.addToCart,
  updateCustomerZipCode: dispatch.customer.updateCustomerZipCode,
})

export default connect(mapState, mapDispatch)(ProductDetailsPage)
